<template>
  <el-dialog
    :model-value="visible"
    title="上传素材"
    width="520px"
    :close-on-click-modal="false"
    @update:model-value="$emit('update:visible', $event)"
  >
    <div v-if="!uploading">
      <div class="upload-drop-zone border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50/30 transition-colors"
        @click="triggerFileInput"
        @dragover.prevent
        @drop.prevent="handleDrop"
      >
        <el-icon :size="40" class="text-slate-300 mb-2"><UploadFilled /></el-icon>
        <p class="text-sm text-slate-500 mb-1">点击或拖拽文件到此处</p>
        <p class="text-xs text-slate-400">
          支持图片、视频、文档、压缩包等
        </p>
        <p class="text-xs text-slate-400 mt-1">
          图片最大 20MB，视频最大 500MB，文件最大 100MB
        </p>
        <input ref="fileInput" type="file" class="hidden" @change="handleFileSelect" />
      </div>

      <div v-if="selectedFile" class="mt-3 p-3 bg-slate-50 rounded flex items-center gap-3">
        <el-icon :size="20" class="text-slate-400"><Document /></el-icon>
        <div class="flex-1 min-w-0">
          <p class="text-sm text-slate-700 truncate">{{ selectedFile.name }}</p>
          <p class="text-xs text-slate-400">{{ formatSize(selectedFile.size) }}</p>
        </div>
        <el-button text size="small" type="danger" @click="selectedFile = null">移除</el-button>
      </div>

      <div class="mt-3" v-if="!props.assetId">
        <label class="text-xs text-slate-500 mb-1 block">素材名称</label>
        <el-input v-model="assetName" placeholder="输入素材名称" maxlength="200" />
      </div>

      <div class="mt-3" v-if="!props.assetId && props.tagList.length > 0">
        <label class="text-xs text-slate-500 mb-1 block">标签</label>
        <el-select v-model="selectedTagIds" multiple placeholder="选择标签" class="w-full" clearable>
          <el-option v-for="tag in props.tagList" :key="tag.id" :label="tag.name" :value="tag.id" />
        </el-select>
      </div>
    </div>

    <div v-else class="text-center py-8">
      <el-progress :percentage="uploadProgress" :stroke-width="8" :status="uploadStatus" />
      <p class="text-sm text-slate-500 mt-3">{{ uploadMessage }}</p>
    </div>

    <template #footer>
      <el-button @click="handleCancel" :disabled="uploading">取消</el-button>
      <el-button type="primary" @click="handleUpload" :disabled="!selectedFile || uploading" :loading="uploading">
        上传
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import { UploadFilled, Document } from '@element-plus/icons-vue'
import { createUploadSession, confirmUploadSession, cancelUploadSession } from '@/api/assets'
import axios from 'axios'

const props = defineProps({
  visible: { type: Boolean, default: false },
  folderId: { type: [Number, String], default: null },
  assetId: { type: [Number, String], default: null },
  tagList: { type: Array, default: () => [] }
})

const emit = defineEmits(['update:visible', 'success'])

const fileInput = ref(null)
const selectedFile = ref(null)
const assetName = ref('')
const selectedTagIds = ref([])

const uploading = ref(false)
const uploadProgress = ref(0)
const uploadStatus = ref('')
const uploadMessage = ref('')
const currentSessionId = ref(null)

function triggerFileInput() {
  fileInput.value?.click()
}

function handleFileSelect(e) {
  const file = e.target.files?.[0]
  if (file) {
    selectedFile.value = file
    // 自动填名称
    if (!assetName.value) {
      const dotIdx = file.name.lastIndexOf('.')
      assetName.value = dotIdx > 0 ? file.name.substring(0, dotIdx) : file.name
    }
  }
}

function handleDrop(e) {
  const file = e.dataTransfer?.files?.[0]
  if (file) {
    selectedFile.value = file
    if (!assetName.value) {
      const dotIdx = file.name.lastIndexOf('.')
      assetName.value = dotIdx > 0 ? file.name.substring(0, dotIdx) : file.name
    }
  }
}

function getAssetType(file) {
  if (file.type.startsWith('image/')) return 'IMAGE'
  if (file.type.startsWith('video/')) return 'VIDEO'
  return 'FILE'
}

function formatSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
  if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB'
  return (bytes / 1073741824).toFixed(1) + ' GB'
}

async function handleUpload() {
  if (!selectedFile.value) return
  const file = selectedFile.value
  const assetType = getAssetType(file)

  uploading.value = true
  uploadProgress.value = 0
  uploadStatus.value = ''
  uploadMessage.value = '正在申请上传凭证...'

  try {
    // Step 1: 申请上传凭证
    const sessionData = {
      folderId: props.folderId,
      assetId: props.assetId || undefined,
      assetType,
      originalName: file.name,
      mimeType: file.type || 'application/octet-stream',
      fileSize: file.size
    }
    const credential = await createUploadSession(sessionData)
    currentSessionId.value = credential.sessionId

    // Step 2: 直传 OSS
    uploadMessage.value = '正在上传文件...'
    await axios.put(credential.uploadUrl, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        ...(credential.headers || {})
      },
      onUploadProgress: (e) => {
        if (e.total) {
          uploadProgress.value = Math.round((e.loaded / e.total) * 90)
        }
      }
    })

    // Step 3: 确认上传
    uploadMessage.value = '正在创建素材...'
    uploadProgress.value = 92
    await confirmUploadSession(credential.sessionId, {
      uploadToken: credential.uploadToken,
      name: assetName.value || undefined,
      tagIds: selectedTagIds.value,
      changeRemark: props.assetId ? '上传新版本' : undefined
    })
    currentSessionId.value = null

    uploadProgress.value = 100
    uploadStatus.value = 'success'
    uploadMessage.value = '上传完成'

    ElMessage.success(props.assetId ? '新版本上传成功' : '素材上传成功')

    setTimeout(() => {
      selectedFile.value = null
      assetName.value = ''
      selectedTagIds.value = []
      uploading.value = false
      uploadProgress.value = 0
      emit('success')
    }, 800)
  } catch (err) {
    await cancelPendingSession()
    uploadStatus.value = 'exception'
    uploadMessage.value = '上传失败'
    uploading.value = false
  }
}

async function cancelPendingSession() {
  if (!currentSessionId.value) return
  const sessionId = currentSessionId.value
  currentSessionId.value = null
  try {
    await cancelUploadSession(sessionId)
  } catch {
    // 取消失败不阻塞用户关闭或重试上传
  }
}

async function handleCancel() {
  if (uploading.value) return
  await cancelPendingSession()
  selectedFile.value = null
  assetName.value = ''
  selectedTagIds.value = []
  emit('update:visible', false)
}
</script>
